import { Sheet } from '@unistocks/components/sheet/sheet';
import React, { useEffect, useRef } from 'react';
import {
  Adapt,
  isWeb,
  Popover,
  PopoverProps,
  styled,
  useComposedRefs,
  withStaticProperties
} from 'tamagui';

export type DatePickerPopoverRef = React.ComponentRef<typeof Popover>;
export type DatePickerPopoverProps = PopoverProps & { ref?: React.Ref<DatePickerPopoverRef> };

const StyledPopover: React.FC<DatePickerPopoverProps> = ({ ref, children, ...rest }) => {
  const popoverRef = useRef<DatePickerPopoverRef>(null);
  const composedRef = useComposedRefs(popoverRef, ref);

  // hide date picker on scroll (web)
  useEffect(() => {
    if (isWeb) {
      const controller = new AbortController();
      // NOTE: For cross-browser compatibility:
      // - We use document.addEventListener('scroll', ...) instead of document.body.addEventListener because Safari does not fire scroll events on body.
      // - We use capture: true because Chrome only fires scroll events on document in the capture phase.
      //   (Chrome works with document.body.addEventListener and capture: false, but that is not reliable in Safari.)
      // This combination ensures the scroll event is caught in both Chrome and Safari.
      document.addEventListener(
        'scroll',
        () => {
          popoverRef.current?.close();
        },
        {
          capture: true,
          signal: controller.signal
        }
      );

      return () => {
        controller.abort();
      };
    }
  }, []);

  return (
    <Popover
      ref={composedRef}
      keepChildrenMounted={'lazy'}
      stayInFrame
      allowFlip
      placement="bottom"
      size="$4"
      {...rest}
    >
      {children}

      <Adapt when="max-md">
        <Sheet enableDynamicSizing showHandle={false}>
          <Adapt.Contents />
        </Sheet>
      </Adapt>
    </Popover>
  );
};

const StyledPopoverContent = styled(Popover.Content, {
  name: 'DatePickerPopoverContent',
  transition: 'quickLessBouncy',
  variants: {
    unstyled: {
      false: {
        padding: '$3',
        borderWidth: 1,
        borderColor: '$borderColor',
        boxShadow: '0px 5px 20px 0px $shadowColor',
        enterStyle: { y: -20, o: 0 },
        exitStyle: { y: -20, o: 0 }
      }
    }
  } as const,

  defaultVariants: {
    unstyled: process.env.TAMAGUI_HEADLESS === '1'
  }
});

export const DatePickerPopover = withStaticProperties(StyledPopover, {
  Trigger: Popover.Trigger,
  Anchor: Popover.Anchor,
  Content: withStaticProperties(StyledPopoverContent, {
    Arrow: styled(Popover.Arrow, {
      borderWidth: 1,
      borderColor: '$borderColor'
    })
  })
});

DatePickerPopover.displayName = 'DatePickerPopover';
